<template>
    <div>
        <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
            <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="updateAlertConfig">
                <v-card>
                    <v-card-title id="heading" class="d-flex justify-space-between">
                        <h4>{{ config?.name || 'Device' }} Config</h4>
                        <v-btn density="compact" icon="mdi-close" variant="flat"
                               @click="$emit('update:modelValue', false)"/>
                    </v-card-title>

                    <v-container>
                        <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                                 density="compact" variant="tonal">
                            <p v-for="(error, i) in errors" :key="i">{{ error }}</p>
                        </v-alert>

                        <v-row>
                            <v-col class="py-0 append-btn" cols="12" sm="6">
                                <v-select v-model="formData['monitoring_notification_group_id']"
                                          :items="notificationGroups"
                                          :loading="!notificationGroups.length"
                                          :rules="[vRules.required]"
                                          density="compact" item-title="label" item-value="id"
                                          label="Notification Recipients">
                                    <template v-slot:append>
                                        <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                                :disabled="!formData['company_id']"
                                                @click="recipientsDialog = true"/>
                                    </template>
                                </v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <h4>Configs</h4>
                            </v-col>

                            <v-col
                                v-for="template in configDetails?.templates || []"
                                :key="template.alert_status_id"
                                cols="4"
                            >
                                <v-checkbox
                                    v-model="formData.templates[template.alert_status_id]"
                                    :value="template.status"
                                    :label="systemAlertRuleLabel(template)"
                                />
                            </v-col>
                        </v-row>
                    </v-container>

                    <v-card-actions class="px-4 d-flex justify-end">
                        <v-btn size="small" type="button" @click="$emit('update:modelValue', false)">
                            Close
                        </v-btn>
                        <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <!-- Recipients dialog -->
        <Recipients
            v-if="recipientsDialog"
            v-model="recipientsDialog"
            :company="formData.company_id"
            @addGroup="onAddGroup"
        />
    </div>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Recipients from "@/components/dialogs/monitoring/notifications/Recipients.vue";
import {systemAlertRuleLabel} from "@/composables/alertTypes";

const props = defineProps({
    modelValue: Boolean,
    config: {type: Object, required: true},
});

const emits = defineEmits(["update:modelValue", "updateConfig"]);

const {user} = useAuthStore();
const {showSnackbar} = useSnackbarStore();

const dialogSwitch = ref(props.modelValue);
const recipientsDialog = ref(false);
const selectedRowIndex = ref(null);

const errors = ref([]);
const alertTemplates = ref([]);
const configDetails = ref(null);
const notificationGroups = ref([]);

const formData = reactive({
    valid: false,
    system_alerts: true,
    name: props.config?.name || "",
    templates: props.config?.templates || {},
    company_id: props.config?.company_id || null,
    monitoring_notification_group_id: props.config?.monitoring_notification_group_id || null,
});

const vRules = {
    required: (v) => !!v || "This field is required",
};

onMounted(async () => {
    await fetchConfigDetails();
    fetchNotificationGroups(false);
});

const fetchConfigDetails = () => {
    return send("get", `monitoring/configs/${props.config.id}`)
        .then((response) => {
            configDetails.value = response.data.data;
            if (configDetails.value?.templates.length) {
                configDetails.value.templates.forEach(template => {
                    formData['templates'][template.alert_status_id] = !!template.alert_status
                })
            }
        })
        .catch((error) => {
            console.error(error);
            showSnackbar("Monitoring config details could not be loaded", "error");
        });
};

const fetchNotificationGroups = (reset = true) => {
    if (reset) formData.monitoring_notification_group_id = null;

    send("get", "monitoring/notification-groups?", {
        list: true,
        company: formData.company_id,
    })
        .then((response) => {
            notificationGroups.value = response.data.data;
        })
        .catch((error) => {
            console.error(error);
            showSnackbar("Monitoring notification groups could not be loaded", "error");
        });
};

const updateAlertConfig = () => {
    errors.value = [];
    send("put", `monitoring/configs/${props.config.id}`, formData)
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                emits("updateConfig", response.data.data);
                emits("update:modelValue", false);
            }
        })
        .catch((errorResponse) => {
            const data = errorResponse.response?.data || {};
            if (data.errors) {
                for (const key in data.errors) {
                    errors.value.push(data.errors[key][0]);
                }
            } else if (data.message) {
                errors.value.push(data.message);
            }
            showSnackbar("Something went wrong", "error");
            document.getElementById("heading")?.scrollIntoView({behavior: "smooth"});
        });
};

// ---- HELPERS ----

const setTemplateValue = (prop = "alert_template", val) => {
    switch (prop) {
        case "alert_template":
            if (formData.templates?.[selectedRowIndex.value])
                formData.templates[selectedRowIndex.value].alert_template = val;
            break;
        case "time_schedule":
            if (formData.templates?.[selectedRowIndex.value])
                formData.templates[selectedRowIndex.value].time_schedule = val;
            break;
        case "notification_group":
            formData.monitoring_notification_group_id = val;
            break;
    }
};

const onAddGroup = (newNotificationGroup) => {
    setTemplateValue("notification_group", newNotificationGroup.id);
    fetchNotificationGroups(false);
};
</script>

<style scoped>
.append-btn .v-input__append {
    margin: 0;
}
</style>
